import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardDescription, CardTitle, Input } from "campaign-builder/ui";
import {
  validateCampaignName,
  normalizeCampaignName,
  getEarliestLaunchDate,
  getDatePickerDateFormat,
  toPersistedLaunchDate,
} from "campaign-builder/state";
import { useMinLeadTimeBusinessDays } from "campaign-builder/hooks/useMinLeadTimeBusinessDays";
import { checkIsCampaignNameExist } from "graphQL/query/campaign";
import { updateCampaign } from "graphQL/mutations/campaign";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { useSaveStatus, trackSave } from "./saveStatus";
import { useTargetingValidation } from "./targetingValidation";
import { useTargetingFocus } from "./targetingFocus";
import { LaunchDatePicker } from "./LaunchDatePicker";
import { FieldLabel, RequiredLabel, FieldError } from "./fields";

const DUPLICATE_NAME_MESSAGE = "Duplicated campaign name. Please choose another.";
const LAUNCH_DATE_REQUIRED_MESSAGE = "Launch date is required";

interface CampaignDetailsCardProps {
  // D2D collects a single launch date here; H2H schedules per-area later (F6), so
  // it shows the name field only.
  showLaunchDate?: boolean;
}

export const CampaignDetailsCard = ({ showLaunchDate = false }: CampaignDetailsCardProps) => {
  const { campaignData, campaignId, updateCampaignState } = useCampaignData();
  const { clientId } = useParams<{ clientId: string }>();
  const { country } = useClientConfig();
  const save = useSaveStatus();
  const { version: validationVersion } = useTargetingValidation();
  const lastValidation = useRef(0);
  const { request: focus, clear: clearFocus } = useTargetingFocus();
  const lastFocusNonce = useRef(0);

  const [nameError, setNameError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  // Bumped when the Edit-launch-date link asks to reveal the picker (F9, AC36).
  const [openDateSignal, setOpenDateSignal] = useState<number | undefined>(undefined);

  const name = campaignData?.campaignName ?? "";
  const selectedDate = campaignData?.startDate ? new Date(Number(campaignData.startDate)) : null;
  const minLeadBusinessDays = useMinLeadTimeBusinessDays();
  const dateFormat = getDatePickerDateFormat(country?.code);

  // Latest campaignData, so an in-flight name save reflects only the name back onto
  // current state instead of reverting fields edited during the request.
  const campaignDataRef = useRef(campaignData);
  campaignDataRef.current = campaignData;

  // Guards the async duplicate-name lookup against races: only the latest blur's
  // result is allowed to set state.
  const nameCheckSeq = useRef(0);

  // Flash field errors when the user tries to advance with the step incomplete.
  useEffect(() => {
    if (validationVersion === lastValidation.current) return;
    lastValidation.current = validationVersion;
    setNameError(validateCampaignName(name));
    if (showLaunchDate && !selectedDate) setDateError(LAUNCH_DATE_REQUIRED_MESSAGE);
  }, [validationVersion, name, selectedDate, showLaunchDate]);

  // The outdated-dates warning's Edit link (D2D) returns here and opens the picker.
  // The request is consumed once and cleared so a step remount (the provider now
  // outlives the step, PAD-31560) can't reopen the picker on every return.
  useEffect(() => {
    if (!focus || focus.target !== "launchDate" || focus.nonce === lastFocusNonce.current) return;
    lastFocusNonce.current = focus.nonce;
    requestAnimationFrame(() => {
      document
        .querySelector('[data-targeting-section="details"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    setOpenDateSignal(focus.nonce);
    clearFocus();
  }, [focus, clearFocus]);

  const handleNameChange = (value: string) => {
    setNameError(validateCampaignName(value));
    if (campaignData) {
      updateCampaignState({ ...campaignData, campaignName: value });
    }
  };

  const handleNameBlur = async () => {
    const localError = validateCampaignName(name);
    setNameError(localError);
    if (localError || !campaignId) return;

    const normalized = normalizeCampaignName(name);
    const seq = (nameCheckSeq.current += 1);
    let isDuplicate = false;
    try {
      const { campaignIds } = await checkIsCampaignNameExist(clientId, normalized);
      const otherIds = campaignIds.map(String).filter((id) => id !== String(campaignId));
      isDuplicate = otherIds.length > 0;
    } catch {
      // A failed uniqueness lookup must not block saving — fall through and persist.
    }
    if (seq !== nameCheckSeq.current) return; // a newer blur superseded this one
    if (isDuplicate) {
      setNameError(DUPLICATE_NAME_MESSAGE);
      return;
    }

    void trackSave(save, async () => {
      await updateCampaign({ campaignId, name: normalized });
      // Reflect the normalized name locally only after the save succeeds, merging
      // onto the latest state so concurrent edits aren't reverted. Skip if the name
      // changed mid-save so a stale request can't clobber a newer value.
      const latest = campaignDataRef.current;
      if (latest && latest.campaignName === name && normalized !== name) {
        updateCampaignState({ ...latest, campaignName: normalized });
      }
    });
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      setDateError(LAUNCH_DATE_REQUIRED_MESSAGE);
      // Clear the stored date too, so the D2D completion gate (!!startDate) and the
      // UI stay in sync if the date is ever cleared.
      if (campaignData) {
        updateCampaignState({ ...campaignData, startDate: null });
      }
      return;
    }
    setDateError(null);
    if (campaignData) {
      updateCampaignState({ ...campaignData, startDate: String(date.getTime()) });
    }
    if (campaignId) {
      void trackSave(save, () => updateCampaignExtraData({ campaignId, startDate: toPersistedLaunchDate(date) }));
    }
  };

  return (
    <Card className="flex flex-col gap-5 p-[25px]" data-targeting-section="details">
      <div className="flex flex-col gap-2">
        <CardTitle>Campaign Details</CardTitle>
        <CardDescription>Start by setting a name for your campaign.</CardDescription>
      </div>
      <div className="flex flex-col gap-1">
        <FieldLabel>Campaign name</FieldLabel>
        <Input
          value={name}
          onChange={(event) => handleNameChange(event.target.value)}
          onBlur={handleNameBlur}
          className={nameError ? "border-destructive" : undefined}
        />
        <FieldError message={nameError} />
      </div>
      {showLaunchDate && (
        <div className="flex flex-col gap-1">
          <RequiredLabel>Launch date</RequiredLabel>
          <LaunchDatePicker
            value={selectedDate}
            onChange={handleDateChange}
            minDate={getEarliestLaunchDate(minLeadBusinessDays)}
            dateFormat={dateFormat}
            error={!!dateError}
            openSignal={openDateSignal}
          />
          <FieldError message={dateError} />
        </div>
      )}
    </Card>
  );
};
